import React, { useEffect, useImperativeHandle, useCallback } from "react";

import API, { graphqlOperation } from "@aws-amplify/api";

import { listArticlesByDate, getArticle } from "../../graphql/queries";
import { onCreateArticle, onUpdateArticle, onDeleteArticle } from "../../graphql/subscriptions";

const ArticleProvider = React.forwardRef((props, ref) => {
  const {
    user,
    articles,
    dispatchArticle,
    articlesNextToken,
    setArticlesNextToken,
    setIsLoading,
    putScrollSession,
  } = props;

  const ARTICLE_CREATE_SUBSCRIPTION = "CREATE_SUBSCRIPTION";
  const ARTICLE_UPDATE_SUBSCRIPTION = "UPDATE_SUBSCRIPTION";
  const ARTICLE_DELETE_SUBSCRIPTION = "DELETE_SUBSCRIPTION";
  const ARTICLE_INITIAL_QUERY = "INITIAL_QUERY";
  const ARTICLE_ADDITIONAL_QUERY = "ADDITIONAL_QUERY";

  const getArticles = useCallback(
    async (type, nextToken = null) => {
      try {
        const res = await API.graphql(
          graphqlOperation(listArticlesByDate, {
            owner: user.username,
            sortDirection: "DESC",
            limit: 8,
            nextToken: nextToken,
          })
        );

        // データの妥当性チェック
        const items = res?.data?.listArticlesByDate?.items || [];
        const validItems = items.filter((item) => item && item.id);

        setArticlesNextToken(res.data.listArticlesByDate.nextToken);
        dispatchArticle({
          type: type,
          articles: validItems,
        });
        setIsLoading(false);
      } catch (error) {
        console.error("Error fetching articles:", error);
        setIsLoading(false);
      }
    },
    [user.username, setArticlesNextToken, dispatchArticle, setIsLoading]
  );

  const getAdditionalArticles = () => {
    // setIsLoading(true);
    if (articlesNextToken === null) return; //Reached the last page
    getArticles(ARTICLE_ADDITIONAL_QUERY, articlesNextToken);

    putScrollSession();
  };

  const fetchArticle = useCallback(
    async (articleId) => {
      await API.graphql(
        graphqlOperation(getArticle, {
          id: articleId,
        })
      )
        .then((result) => {
          dispatchArticle({
            type: ARTICLE_CREATE_SUBSCRIPTION,
            article: result.data.getArticle,
          });
        })
        .catch((err) => console.error("Error fetching article:", err));
    },
    [dispatchArticle]
  );

  useEffect(() => {
    let isMounted = true;
    setIsLoading(true);

    const variables = {
      owner: user.username,
    };

    if (!articles || !articles.length) {
      getArticles(ARTICLE_INITIAL_QUERY);
    } else {
      setIsLoading(false);
    }

    const createArticleSubscription = API.graphql(
      graphqlOperation(onCreateArticle, variables)
    ).subscribe({
      next: (msg) => {
        if (!isMounted) return;
        try {
          const article = msg?.value?.data?.onCreateArticle;
          if (!article || article.owner !== user.username) return;
          fetchArticle(article.id);
        } catch (err) {
          console.error("Error processing create subscription message:", err);
        }
      },
      error: (err) => {
        console.error("Error in create subscription:", err);
      },
    });

    const updateArticleSubscription = API.graphql(
      graphqlOperation(onUpdateArticle, variables)
    ).subscribe({
      next: (msg) => {
        if (!isMounted) return;
        try {
          const article = msg?.value?.data?.onUpdateArticle;
          if (!article || article.owner !== user.username) return;
          dispatchArticle({
            type: ARTICLE_UPDATE_SUBSCRIPTION,
            article: article,
          });
        } catch (err) {
          console.error("Error processing update subscription message:", err);
        }
      },
      error: (err) => {
        console.error("Error in update subscription:", err);
      },
    });

    const deleteArticleSubscription = API.graphql(
      graphqlOperation(onDeleteArticle, variables)
    ).subscribe({
      next: (msg) => {
        if (!isMounted) return;
        try {
          const article = msg?.value?.data?.onDeleteArticle;
          if (!article || article.owner !== user.username) return;
          dispatchArticle({
            type: ARTICLE_DELETE_SUBSCRIPTION,
            article: article,
          });
        } catch (err) {
          console.error("Error processing delete subscription message:", err);
        }
      },
      error: (err) => {
        console.error("Error in delete subscription:", err);
      },
    });

    return () => {
      isMounted = false;
      putScrollSession();
      createArticleSubscription.unsubscribe();
      updateArticleSubscription.unsubscribe();
      deleteArticleSubscription.unsubscribe();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useImperativeHandle(ref, () => ({
    getAdditionalArticles,
  }));

  return <React.Fragment>{props.children}</React.Fragment>;
});

export default ArticleProvider;
